import { applyColorStyle } from "@/shared/utils/apply-color-style";
import { cn } from "@lib/cn";
import {
  IconCalendarTime,
  IconChecks,
  IconCode,
  IconColorPicker,
  IconDatabase,
  IconFileTextAi,
  IconIcons,
  IconLetterCase,
  IconListDetails,
  IconMathFunction,
  IconNumber123,
  IconPhoto,
  IconPhotoVideo,
  IconSelect,
  IconTable,
  IconVariable,
  IconVideo,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import type { VariableValueType } from "../types";
import { FathomIcon } from "./fathom-icon";
import { HubspotIcon } from "./hubspot-icon";

type IconComponent = ComponentType<{ className?: string; stroke?: number }>;

const TYPE_ICONS: Record<VariableValueType, IconComponent> = {
  input: IconLetterCase,
  computed: IconLetterCase,
  number: IconNumber123,
  image: IconPhoto,
  video: IconVideo,
  media: IconPhotoVideo,
  icon: IconIcons,
  ai: IconFileTextAi,
  select: IconSelect,
  multiselect: IconChecks,
  list: IconListDetails,
  date: IconCalendarTime,
  color: IconColorPicker,
  data: IconTable,
  iframe: IconCode,
  system: IconDatabase,
  hubspot: HubspotIcon,
  fathom: FathomIcon,
  calculation: IconMathFunction,
};

// Calculation variables get a green accent everywhere else uses tw-text-blue
// (Figma 83:45625 — the Σ formula type reads as a distinct "computed" kind).
const TYPE_COLOR_CLASS: Partial<Record<VariableValueType, string>> = {
  calculation: "tw-text-green-600",
};

interface Props {
  type: VariableValueType;
  className?: string;
  /**
   * `type: "color"` only — the variable's own color, rendered as a filled
   * swatch instead of the generic color-picker glyph (Figma 90:56298).
   * Absent/unset falls back to a neutral swatch.
   */
  color?: string;
}

/** Leading icon for a variable row, driven by the variable's type. */
export const VariableTypeIcon = ({ type, className, color }: Props) => {
  // A color variable with a resolved value reads as a filled swatch + glyph
  // (Figma 90:56298). Without a value (variables page, type picker) fall back
  // to the generic color-picker glyph rather than an empty neutral swatch.
  if (type === "color" && color) {
    return (
      <span className={cn("tw-flex tw-shrink-0 tw-items-center tw-gap-1", className)}>
        <span
          className="tw-size-3 tw-shrink-0 tw-rounded-full tw-border tw-border-solid tw-border-blue/10"
          style={applyColorStyle(color, { target: "background" })}
        />
        <IconVariable stroke={1.5} className="tw-size-3.5 tw-shrink-0 tw-text-blue" />
      </span>
    );
  }

  const Icon = TYPE_ICONS[type] ?? IconLetterCase;
  return (
    <Icon
      stroke={1.5}
      className={cn(
        "tw-size-4 tw-shrink-0",
        TYPE_COLOR_CLASS[type] ?? "tw-text-blue",
        className,
      )}
    />
  );
};
